
import { toast } from 'sonner';
import {
  hashPassword,
  savePasswordToStorage,
  createAuthSession,
  saveLockoutInfo,
  calculateLockoutEndTime,
  validatePassword,
  clearAuthSession,
  getSecurePassKey,
  playNotificationSound
} from '../utils';

export const usePasswordActions = (state: {
  hasPassword: boolean;
  setHasPassword: (value: boolean) => void;
  passwordHash: string | null;
  setPasswordHash: (value: string | null) => void;
  remainingAttempts: number;
  setRemainingAttempts: (value: number) => void;
  isLocked: boolean;
  setIsLocked: (value: boolean) => void;
  lockoutEndTime: Date | null;
  setLockoutEndTime: (value: Date | null) => void;
}) => {
  const {
    hasPassword,
    setHasPassword,
    passwordHash,
    setPasswordHash,
    remainingAttempts,
    setRemainingAttempts,
    setIsLocked,
    setLockoutEndTime,
  } = state;

  const createPassword = (password: string) => {
    const validation = validatePassword(password);
    if (!validation.isValid) {
      toast.error(validation.message);
      return;
    }

    const hash = hashPassword(password);
    setPasswordHash(hash);
    setHasPassword(true);
    savePasswordToStorage(hash);
    createAuthSession();
    toast.success("Password created successfully");
  };

  const verifyPassword = (password: string): boolean => {
    if (state.isLocked) return false;

    const isCorrect = hashPassword(password) === passwordHash;
    
    if (!isCorrect) {
      const newRemainingAttempts = remainingAttempts - 1;
      setRemainingAttempts(newRemainingAttempts);
      saveLockoutInfo(newRemainingAttempts, state.lockoutEndTime);
      
      if (newRemainingAttempts <= 0) {
        lockAccount();
      }
      return false;
    }
    
    // Reset attempts on successful login
    setRemainingAttempts(3);
    saveLockoutInfo(3, null);
    createAuthSession();
    return true;
  };

  const resetPassword = (currentPassword: string | null, newPassword: string): boolean => {
    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      toast.error(validation.message);
      return false;
    }

    if (!hasPassword || (currentPassword && hashPassword(currentPassword) === passwordHash)) {
      const hash = hashPassword(newPassword);
      setPasswordHash(hash);
      setHasPassword(true);
      savePasswordToStorage(hash);
      createAuthSession();
      toast.success("Password reset successfully");
      return true;
    }
    
    toast.error("Current password is incorrect");
    return false;
  };

  const clearPassword = () => {
    setPasswordHash(null);
    setHasPassword(false);
    localStorage.removeItem('nalc_auth_password');
    localStorage.removeItem('nalc_auth_salt');
    clearAuthSession();
    toast.success("Password removed");
  };

  const lockAccount = () => {
    setIsLocked(true);
    const endTime = calculateLockoutEndTime();
    setLockoutEndTime(endTime);
    saveLockoutInfo(0, endTime);
    clearAuthSession();
    toast.error("Too many failed attempts. Account locked for 10 minutes.");
  };

  const passwordStrength = (password: string) => {
    return validatePassword(password);
  };

  const verifySecurePassKeyAndAuth = (passkey: string): boolean => {
    console.log('Verifying secure passkey:', passkey);
    console.log('Expected passkey:', 'EDGTU70HFDJ73GDJAW124GFSVSH73H');
    
    // Direct comparison with the known passkey
    const isValid = passkey === 'EDGTU70HFDJ73GDJAW124GFSVSH73H';
    
    if (isValid) {
      // Reset attempts and create auth session
      setRemainingAttempts(3);
      saveLockoutInfo(3, null);
      setIsLocked(false);
      createAuthSession();
      playNotificationSound();
      toast.success("Emergency access granted with VIP Pass Key!");
      return true;
    }
    
    toast.error("Invalid VIP Pass Key");
    console.log('Passkey verification failed');
    return false;
  };

  const hasSecurePassKey = (): boolean => {
    return true; // Always return true to ensure the feature is available
  };

  return {
    createPassword,
    verifyPassword,
    resetPassword,
    clearPassword,
    lockAccount,
    passwordStrength,
    verifySecurePassKey: verifySecurePassKeyAndAuth,
    hasSecurePassKey,
  };
};
